export const supportedUiLanguages = ["en", "fr"] as const;
export type SupportedUiLanguage = (typeof supportedUiLanguages)[number];

export const countryCatalog = {
  MA: {
    code: "MA",
    name: "Morocco",
    defaultUiLanguage: "fr",
    defaultFormatLocale: "fr-MA",
    defaultTimezone: "Africa/Casablanca",
    timezoneOptions: ["Africa/Casablanca"],
    defaultCurrency: "MAD",
    regionLabel: "Region",
  },
  FR: {
    code: "FR",
    name: "France",
    defaultUiLanguage: "fr",
    defaultFormatLocale: "fr-FR",
    defaultTimezone: "Europe/Paris",
    timezoneOptions: ["Europe/Paris"],
    defaultCurrency: "EUR",
    regionLabel: "Region",
  },
  US: {
    code: "US",
    name: "United States",
    defaultUiLanguage: "en",
    defaultFormatLocale: "en-US",
    defaultTimezone: "America/New_York",
    timezoneOptions: [
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Phoenix",
      "America/Los_Angeles",
      "America/Anchorage",
      "Pacific/Honolulu",
    ],
    defaultCurrency: "USD",
    regionLabel: "State",
  },
  GB: {
    code: "GB",
    name: "United Kingdom",
    defaultUiLanguage: "en",
    defaultFormatLocale: "en-GB",
    defaultTimezone: "Europe/London",
    timezoneOptions: ["Europe/London"],
    defaultCurrency: "GBP",
    regionLabel: "Region",
  },
  CA: {
    code: "CA",
    name: "Canada",
    defaultUiLanguage: "en",
    defaultFormatLocale: "en-CA",
    defaultTimezone: "America/Toronto",
    timezoneOptions: [
      "America/Toronto",
      "America/Winnipeg",
      "America/Edmonton",
      "America/Vancouver",
      "America/Halifax",
      "America/St_Johns",
    ],
    defaultCurrency: "CAD",
    regionLabel: "Province",
  },
  AE: {
    code: "AE",
    name: "United Arab Emirates",
    defaultUiLanguage: "en",
    defaultFormatLocale: "en-AE",
    defaultTimezone: "Asia/Dubai",
    timezoneOptions: ["Asia/Dubai"],
    defaultCurrency: "AED",
    regionLabel: "Emirate",
  },
  SA: {
    code: "SA",
    name: "Saudi Arabia",
    defaultUiLanguage: "en",
    defaultFormatLocale: "en-SA",
    defaultTimezone: "Asia/Riyadh",
    timezoneOptions: ["Asia/Riyadh"],
    defaultCurrency: "SAR",
    regionLabel: "Province",
  },
  ES: {
    code: "ES",
    name: "Spain",
    defaultUiLanguage: "en",
    defaultFormatLocale: "es-ES",
    defaultTimezone: "Europe/Madrid",
    timezoneOptions: ["Europe/Madrid", "Atlantic/Canary"],
    defaultCurrency: "EUR",
    regionLabel: "Region",
  },
  DE: {
    code: "DE",
    name: "Germany",
    defaultUiLanguage: "en",
    defaultFormatLocale: "de-DE",
    defaultTimezone: "Europe/Berlin",
    timezoneOptions: ["Europe/Berlin"],
    defaultCurrency: "EUR",
    regionLabel: "State",
  },
} as const;

export type CountryCode = keyof typeof countryCatalog;
export type CountryPolicy = (typeof countryCatalog)[CountryCode];
export type CurrencyCode = CountryPolicy["defaultCurrency"];
export type CatalogFormatLocale = CountryPolicy["defaultFormatLocale"];
export type RegionLabel = CountryPolicy["regionLabel"];

export interface GeoDefaults {
  countryCode: CountryCode;
  countryName: string;
  uiLanguage: SupportedUiLanguage;
  formatLocale: string;
  timezone: string;
  currency: CurrencyCode;
  regionLabel: RegionLabel;
}

export interface GeoOverrides {
  uiLanguage?: SupportedUiLanguage;
  formatLocale?: string;
  timezone?: string;
}

export function normalizeCountryCode(value: string): string {
  return value.trim().toUpperCase();
}

export function isSupportedCountryCode(value: string): value is CountryCode {
  return Object.prototype.hasOwnProperty.call(countryCatalog, value);
}

export function getCountryPolicy(value: string): CountryPolicy | undefined {
  const normalized = normalizeCountryCode(value);

  if (!isSupportedCountryCode(normalized)) {
    return undefined;
  }

  return countryCatalog[normalized];
}

export function isSupportedUiLanguage(value: string): value is SupportedUiLanguage {
  return (supportedUiLanguages as readonly string[]).includes(value);
}

export function isValidIanaTimezone(value: string): boolean {
  const normalized = value.trim();

  if (!normalized) {
    return false;
  }

  try {
    new Intl.DateTimeFormat("en", { timeZone: normalized }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function isValidFormatLocale(value: string): boolean {
  const normalized = value.trim();

  if (!normalized) {
    return false;
  }

  try {
    new Intl.Locale(normalized);
    return true;
  } catch {
    return false;
  }
}

export function resolveGeoDefaults(
  countryCode: string,
  overrides: GeoOverrides = {},
): GeoDefaults | undefined {
  const policy = getCountryPolicy(countryCode);

  if (!policy) {
    return undefined;
  }

  const uiLanguage = overrides.uiLanguage ?? policy.defaultUiLanguage;
  const formatLocale = overrides.formatLocale?.trim() || policy.defaultFormatLocale;
  const timezone = overrides.timezone?.trim() || policy.defaultTimezone;

  if (!isSupportedUiLanguage(uiLanguage)) {
    return undefined;
  }

  if (!isValidFormatLocale(formatLocale)) {
    return undefined;
  }

  if (!isValidIanaTimezone(timezone)) {
    return undefined;
  }

  return {
    countryCode: policy.code,
    countryName: policy.name,
    uiLanguage,
    formatLocale,
    timezone,
    currency: policy.defaultCurrency,
    regionLabel: policy.regionLabel,
  };
}

export function formatCurrency(
  amount: number,
  currency: string,
  locale: string,
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(amount);
}

export function formatNumber(
  value: number,
  locale: string,
): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function formatDate(
  value: Date | number,
  locale: string,
  timezone: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeZone: timezone,
  }).format(value);
}

export function formatDateTime(
  value: Date | number,
  locale: string,
  timezone: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  }).format(value);
}
